import { useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Loader } from '@lms/ui';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { api } from '@/lib/api';
import { activityPercentage } from '@/lib/grade-percentage';

interface AcademicDetail {
  id: string;
  coursesEnrolled?: number;
  coursesPassed?: number;
  averageGrade?: number | null;
  hoursCompleted?: number;
  user?: {
    id: string;
    fullName?: string;
    name: string;
    email: string;
    avatarUrl?: string;
    enrollments?: { course: { id: string; name: string; code?: string } | null; status?: string }[];
    studentGrades?: {
      score?: number | null;
      percentage?: number | null;
      status?: string;
      gradeItem?: { title: string; maxScore?: number };
    }[];
  };
}

function initials(name: string): string {
  return name
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p.charAt(0).toUpperCase())
    .join('');
}

function badgeMod(status?: string): string {
  switch (status) {
    case 'passed':
    case 'approved':
      return 'passed';
    case 'failed':
      return 'failed';
    case 'in_progress':
      return 'in-progress';
    case 'pending':
      return 'pending';
    case 'exempt':
      return 'exempt';
    case 'voided':
      return 'voided';
    default:
      return 'default';
  }
}

export function AcademicShowPage({
  userId: userIdProp,
  backTo = '/expediente/admin',
}: {
  userId?: string;
  backTo?: string;
} = {}) {
  const { userId: paramUserId } = useParams<{ userId: string }>();
  const userId = userIdProp ?? paramUserId;
  const { data, isLoading, error } = useQuery({
    queryKey: ['academic-records', userId],
    queryFn: () => api<AcademicDetail>(`/academic-records/${userId}`),
    enabled: Boolean(userId),
  });

  const user = data?.user;
  const studentName = user?.fullName ?? user?.name ?? 'Estudiante';

  const courseRows = useMemo(() => {
    const enrollments = user?.enrollments ?? [];
    const byCourse = new Map<string, { courseName: string; grades: typeof user extends undefined ? never : NonNullable<typeof user>['studentGrades'] }>();

    for (const enrollment of enrollments) {
      if (!enrollment.course) continue;
      byCourse.set(enrollment.course.id, { courseName: enrollment.course.name, grades: [] });
    }

    for (const grade of user?.studentGrades ?? []) {
      const firstCourse = enrollments.find((e) => e.course)?.course;
      const key = firstCourse?.id ?? 'general';
      const row = byCourse.get(key) ?? { courseName: firstCourse?.name ?? '—', grades: [] };
      row.grades = [...(row.grades ?? []), grade];
      byCourse.set(key, row);
    }

    return [...byCourse.entries()].map(([courseId, row]) => {
      const grades = row.grades ?? [];
      const scored = grades.filter((g) => g.score != null);
      const percentages = grades
        .map((grade) =>
          activityPercentage({
            percentage: grade.percentage,
            score: grade.score,
            maxScore: grade.gradeItem?.maxScore,
          }),
        )
        .filter((value): value is number => value != null);
      const avg =
        percentages.length > 0
          ? percentages.reduce((total, value) => total + value, 0) / percentages.length
          : null;
      return {
        courseId,
        courseName: row.courseName,
        finalPercentage: avg,
        letterGrade: '—',
        status: grades[0]?.status,
        statusLabel: grades[0]?.status ?? '—',
        itemsGraded: scored.length,
        itemsTotal: grades.length,
      };
    });
  }, [user]);

  const gradeRows = useMemo(() => {
    const enrollments = user?.enrollments ?? [];
    const defaultCourse = enrollments.find((e) => e.course)?.course?.name ?? '—';
    return (user?.studentGrades ?? []).map((grade) => ({
      course: defaultCourse,
      activity: grade.gradeItem?.title ?? '—',
      score: grade.score,
      maxScore: grade.gradeItem?.maxScore ?? 0,
      percentage: activityPercentage({
        percentage: grade.percentage,
        score: grade.score,
        maxScore: grade.gradeItem?.maxScore,
      }),
      attempts: 0,
    }));
  }, [user]);

  const average = data?.averageGrade ?? null;
  const coursesEnrolled = data?.coursesEnrolled ?? user?.enrollments?.length ?? 0;
  const coursesPassed = data?.coursesPassed ?? 0;
  const hoursCompleted = data?.hoursCompleted ?? 0;
  const backLabel = backTo === '/expediente/admin' ? 'Volver a Expedientes Académicos' : 'Volver';

  return (
    <div className="lms-academic-show lms-site-admin-hub-page font-sans">
      <header className="mb-6 flex items-start gap-4">
        <HubBackLink to={backTo} label={backLabel} />
        <div className="min-w-0">
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Transcripción académica</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            {user ? studentName : 'Registro académico del estudiante'}
          </p>
        </div>
      </header>

      {error && (
        <p className="mb-4 text-sm text-danger-600" role="alert">
          {(error as Error).message}
        </p>
      )}

      {isLoading ? (
        <div className="flex justify-center py-12">
          <Loader />
        </div>
      ) : user ? (
        <>
          <section className="lms-academic-show__profile">
            {user.avatarUrl ? (
              <img src={user.avatarUrl} alt="" className="lms-academic-show__avatar-img" />
            ) : (
              <span className="lms-academic-show__avatar" aria-hidden="true">
                {initials(studentName) || '?'}
              </span>
            )}
            <div className="lms-academic-show__profile-meta">
              <h2 className="lms-academic-show__name">{studentName}</h2>
              <p className="lms-academic-show__email">
                <span className="material-symbols-outlined" aria-hidden="true">
                  mail
                </span>
                {user.email}
              </p>
            </div>
          </section>

          <section className="lms-academic-show__stats" aria-label="Resumen académico">
            <article className="lms-academic-show__stat">
              <div className="lms-academic-show__stat-head">
                <h3>Cursos inscritos</h3>
                <span className="lms-academic-show__stat-icon lms-academic-show__stat-icon--primary">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    menu_book
                  </span>
                </span>
              </div>
              <p className="lms-academic-show__stat-value">{coursesEnrolled}</p>
            </article>

            <article className="lms-academic-show__stat">
              <div className="lms-academic-show__stat-head">
                <h3>Aprobados</h3>
                <span className="lms-academic-show__stat-icon lms-academic-show__stat-icon--secondary">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    task_alt
                  </span>
                </span>
              </div>
              <p className="lms-academic-show__stat-value">{coursesPassed}</p>
            </article>

            <article className="lms-academic-show__stat">
              <div className="lms-academic-show__stat-head">
                <h3>Promedio general</h3>
                <span className="lms-academic-show__stat-icon lms-academic-show__stat-icon--neutral">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    analytics
                  </span>
                </span>
              </div>
              <p
                className={`lms-academic-show__stat-value${average === null ? ' lms-academic-show__stat-value--muted' : ''}`}
              >
                {average !== null ? `${average.toFixed(1)}%` : '—'}
              </p>
            </article>

            <article className="lms-academic-show__stat">
              <div className="lms-academic-show__stat-head">
                <h3>Horas completadas</h3>
                <span className="lms-academic-show__stat-icon lms-academic-show__stat-icon--tertiary">
                  <span className="material-symbols-outlined" aria-hidden="true">
                    schedule
                  </span>
                </span>
              </div>
              <p className="lms-academic-show__stat-value">{hoursCompleted.toFixed(1)}</p>
            </article>
          </section>

          <section className="lms-academic-show__section">
            <h3 className="lms-academic-show__section-title">Cursos</h3>
            <div className="lms-academic-show__table-card">
              <div className="lms-academic-show__table-scroll">
                <table className="lms-academic-show__table">
                  <thead>
                    <tr>
                      <th>Curso</th>
                      <th>Promedio</th>
                      <th>Letra</th>
                      <th>Estado</th>
                      <th>Calificadas</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courseRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="lms-academic-show__empty">
                          <span className="material-symbols-outlined" aria-hidden="true">
                            inbox
                          </span>
                          <p>No hay cursos registrados</p>
                        </td>
                      </tr>
                    ) : (
                      courseRows.map((course) => (
                        <tr key={course.courseId}>
                          <td className="lms-academic-show__course-name">{course.courseName}</td>
                          <td>
                            {course.finalPercentage !== null ? `${course.finalPercentage.toFixed(1)}%` : '—'}
                          </td>
                          <td>{course.letterGrade}</td>
                          <td>
                            <span className={`lms-academic-show__badge lms-academic-show__badge--${badgeMod(course.status)}`}>
                              {course.statusLabel}
                            </span>
                          </td>
                          <td>
                            {course.itemsGraded}/{course.itemsTotal}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>

          <section className="lms-academic-show__section">
            <h3 className="lms-academic-show__section-title">Calificaciones por actividad</h3>
            <div className="lms-academic-show__table-card">
              <div className="lms-academic-show__table-scroll">
                <table className="lms-academic-show__table">
                  <thead>
                    <tr>
                      <th>Curso</th>
                      <th>Actividad</th>
                      <th>Nota</th>
                      <th>%</th>
                      <th>Intentos</th>
                    </tr>
                  </thead>
                  <tbody>
                    {gradeRows.length === 0 ? (
                      <tr>
                        <td colSpan={5} className="lms-academic-show__empty">
                          <span className="material-symbols-outlined" aria-hidden="true">
                            assignment
                          </span>
                          <p>No hay actividades calificadas</p>
                        </td>
                      </tr>
                    ) : (
                      gradeRows.map((grade, index) => (
                        <tr key={index}>
                          <td>{grade.course}</td>
                          <td className="lms-academic-show__activity-name">{grade.activity}</td>
                          <td>
                            {grade.score !== null && grade.score !== undefined
                              ? `${grade.score.toFixed(1)}/${grade.maxScore.toFixed(0)}`
                              : '—'}
                          </td>
                          <td>{grade.percentage !== null ? `${grade.percentage.toFixed(1)}%` : '—'}</td>
                          <td>{grade.attempts}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </section>
        </>
      ) : null}
    </div>
  );
}
